import { useEffect, useRef, useState } from 'react';
import { isValidDateKey } from '../../core/dates';
import {
  currentPeriod,
  customPeriod,
  customRange,
  periodLabel,
  periodRange,
  shiftPeriod,
  switchBasis,
  type Period,
  type PeriodBasis,
} from '../../core/period';

interface Props {
  period: Period;
  today: string;
  onChange: (period: Period) => void;
}

/**
 * The global period selector (WIREFRAMES §1.1).
 *
 * Global rather than per-screen: it governs the dashboard, transactions and reports at
 * once, so switching to a financial year is done once instead of three times.
 *
 * The scope menu and the custom range arrived with M6. Until then the app could only ever
 * show a year — `currentPeriod` defaults to `'year'` and nothing switched it — so the
 * month scope the period model already supported was unreachable from the interface.
 * FR-RPT-01's arbitrary from–to range is the third entry, and it is the one that makes
 * "everything I migrated" (1 Jan – 31 Jul 2026, which straddles the FY boundary)
 * expressible at all.
 */
export function PeriodSelector({ period, today, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const range = customRange(period) ?? periodRange(period);
  const [start, setStart] = useState(range.start);
  const [end, setEnd] = useState(range.end);
  const container = useRef<HTMLDivElement>(null);

  const setBasis = (basis: PeriodBasis) => onChange(switchBasis(period, basis, today));

  // Close on an outside click or Escape. Without this the panel sits over the content
  // until something else is clicked, which on a phone means it covers the screen.
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function choose(next: Period) {
    onChange(next);
    setOpen(false);
  }

  function applyRange() {
    if (!isValidDateKey(start) || !isValidDateKey(end)) return;
    choose(customPeriod(start, end, period.basis));
  }

  const rangeIsValid = isValidDateKey(start) && isValidDateKey(end);

  return (
    <>
      <div className="period" ref={container}>
        <button
          type="button"
          onClick={() => onChange(shiftPeriod(period, -1))}
          aria-label="Previous period"
        >
          ‹
        </button>

        <button
          type="button"
          className="label"
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={() => setOpen((was) => !was)}
        >
          {periodLabel(period)}
        </button>

        <button
          type="button"
          onClick={() => onChange(shiftPeriod(period, 1))}
          aria-label="Next period"
        >
          ›
        </button>

        {open && (
          <div className="period-menu" role="dialog" aria-label="Choose a period">
            <button
              type="button"
              className="menu-item"
              aria-current={period.scope === 'month' ? 'true' : undefined}
              onClick={() => choose(currentPeriod(today, period.basis, 'month'))}
            >
              This month
            </button>
            <button
              type="button"
              className="menu-item"
              aria-current={period.scope === 'year' ? 'true' : undefined}
              onClick={() => choose(currentPeriod(today, period.basis, 'year'))}
            >
              {period.basis === 'fy' ? 'This financial year' : 'This calendar year'}
            </button>

            <div className="menu-sep" />

            <label className="menu-field">
              From
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            </label>
            <label className="menu-field">
              To
              <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
            </label>
            <button
              type="button"
              className="btn btn-primary menu-apply"
              disabled={!rangeIsValid}
              onClick={applyRange}
            >
              Use this range
            </button>
            {/* Stated rather than left to be discovered: a range that crosses a year
                boundary is the main reason this exists. */}
            <p className="muted tiny">Any dates, including across a year boundary.</p>
          </div>
        )}
      </div>

      <div className="basis-toggle" role="group" aria-label="Period basis">
        <button type="button" aria-pressed={period.basis === 'fy'} onClick={() => setBasis('fy')}>
          FY
        </button>
        <button
          type="button"
          aria-pressed={period.basis === 'calendar'}
          onClick={() => setBasis('calendar')}
          aria-label="Calendar year"
        >
          {/* "Calendar" clips at 375px. Both labels ship; CSS picks one. */}
          <span className="wide-only">Calendar</span>
          <span className="narrow-only">Cal</span>
        </button>
      </div>
    </>
  );
}
